import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import '../AdminContentTheme.css';
import './Layout.css';

interface LayoutProps {
  onLogout: () => void;
  username?: string;
}

const Layout: React.FC<LayoutProps> = ({ onLogout, username }) => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const displayName = username || localStorage.getItem('username') || 'Admin';
  const initials = useMemo(() => {
    return (
      displayName
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || 'A'
    );
  }, [displayName]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  const handleNavigate = (path: string) => {
    setIsUserMenuOpen(false);
    navigate(path);
  };

  return (
    <div className={`layout ${isSidebarCollapsed ? 'layout-collapsed' : ''}`}>
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
      />
      <main className="main-content">
        <header className="admin-topbar">
          <div className="admin-topbar-spacer" />
          <div className="admin-user-menu" ref={userMenuRef}>
            <button
              type="button"
              className="admin-user-trigger"
              onClick={() => setIsUserMenuOpen((current) => !current)}
              aria-haspopup="menu"
              aria-expanded={isUserMenuOpen}
            >
              <span className="admin-user-avatar">{initials}</span>
              <span className="admin-user-name">{displayName}</span>
              <span className="admin-user-caret">v</span>
            </button>

            {isUserMenuOpen && (
              <div className="admin-user-dropdown" role="menu">
                <button type="button" role="menuitem" onClick={() => handleNavigate('/settings')}>
                  Settings
                </button>
                <button type="button" role="menuitem" onClick={() => handleNavigate('/advertiser-integrations')}>
                  Advertiser Integration
                </button>
                <button type="button" role="menuitem" onClick={() => handleNavigate('/publisher-view/1')}>
                  Publisher Layout
                </button>
                <button type="button" role="menuitem" onClick={onLogout}>
                  Log out
                </button>
              </div>
            )}
          </div>
        </header>
        <div className="admin-content-scroll">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
